import React, { FC, memo, useContext, useEffect, useState } from "react";
import { Tooltip, Typography } from "@material-ui/core";
import { IndicationApprovalStatus } from "../../typescript/types/data";
import { IndicationApprovalStageType } from "../../typescript/enums/data";
import { makeStyles, withStyles } from "@material-ui/styles";
import { VisibilityContext } from "../../context";
import { OverflowDetails } from "../../typescript/interfaces/data";
import { Info } from "@material-ui/icons";
import { StyledWideTooltip } from "../../utils/StyledWideTooltip";
import { orderBy } from "lodash";

const useStyles = makeStyles({
  open: {
    top: "0px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
  },
  closed: {
    position: "absolute",
    top: "0px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
  },
});

type StageTDProps = {
  indicationApprovalStatus: IndicationApprovalStatus[];
  showCompletionDates?: boolean;
  overflow: OverflowDetails;
  rowId: number;
};
const StageTD: FC<StageTDProps> = memo(({ indicationApprovalStatus, showCompletionDates, overflow, rowId }) => {
  const classes = useStyles();

  const [visibilityContext] = useContext(VisibilityContext);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    visibilityContext.rowsExpanded.includes(rowId) || visibilityContext.detailPanelsOpen.includes(rowId)
      ? setIsOpen(true)
      : setIsOpen(false);
  }, [visibilityContext]);

  return (
    <div className={overflow.isExpandable ? (isOpen ? classes.open : classes.closed) : ""}>
      {orderBy(
        indicationApprovalStatus,
        [(status) => status.stage !== "Withdrawn" && status.stage !== "Discontinued"],
        ["desc"]
      ).map((status, idx) => {
        const { stage, date } = status;

        const getCompletionDate = (s, field, label) =>
          s.stage === IndicationApprovalStageType.PhaseIII &&
          showCompletionDates &&
          s[field]?.date &&
          `${(s[field]?.isActual ? "" : "Expected ") + label}: ${s[field]?.date}`;

        if (stage !== IndicationApprovalStageType.Approved) {
          const showDate = stage === IndicationApprovalStageType.CRL || stage === IndicationApprovalStageType.Pending;

          const completionDates = [
            getCompletionDate(status, "primaryCompletionDate", "Primary Completion Date"),
            getCompletionDate(status, "studyCompletionDate", "Study Completion Date"),
          ].filter((x) => !!x);

          return (
            <div key={idx}>
              <Typography variant="body2">
                <StyledWideTooltip
                  title={
                    status.noRecentDevelopment ? (
                      <>
                        There are no recent developments for this indication. The stage reflects the last known status.
                      </>
                    ) : (
                      ""
                    )
                  }
                >
                  <span style={{ fontStyle: status.noRecentDevelopment ? "italic" : "normal" }}>
                    {stage}
                    {status.noRecentDevelopment && <>*</>}
                  </span>
                </StyledWideTooltip>

                {completionDates.length ? (
                  <StyledWideTooltip
                    title={completionDates.map((cd, i) => (
                      <div key={i}>{cd}</div>
                    ))}
                  >
                    <Info style={{ fontSize: "110%", color: "#2196f3" }} />
                  </StyledWideTooltip>
                ) : (
                  ""
                )}
              </Typography>
              {showDate && <Typography variant="body2">{date ? `(${date})` : ""}</Typography>}
            </div>
          );
        }
      })}
    </div>
  );
});

export default StageTD;
